import { useState, useEffect, useCallback } from 'react';
import { LOGO_SMALL } from '@/assets/logo';
import {
  LogOut, Users, UserCheck, TrendingUp, Inbox,
  Search, ChevronDown, Trash2, MessageSquare,
  Phone, Building2, Mail, Clock, Filter, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-500', textColor: 'text-blue-400', bgLight: 'bg-blue-500/10' },
  { value: 'contacted', label: 'Contacted', color: 'bg-amber-500', textColor: 'text-amber-400', bgLight: 'bg-amber-500/10' },
  { value: 'converted', label: 'Converted', color: 'bg-emerald-500', textColor: 'text-emerald-400', bgLight: 'bg-emerald-500/10' },
  { value: 'lost', label: 'Lost', color: 'bg-red-500', textColor: 'text-red-400', bgLight: 'bg-red-500/10' },
];

function getStatusStyle(status) {
  return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AdminDashboard({ onLogout }) {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedContact, setSelectedContact] = useState(null);
  const [notes, setNotes] = useState('');

  const token = localStorage.getItem('vertex_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [contactsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setContacts(contactsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateContact = async (id, data) => {
    try {
      await axios.patch(`${API}/admin/contacts/${id}`, data, { headers });
      fetchData();
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers });
      setSelectedContact(null);
      fetchData();
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    }
  };

  const filteredContacts = contacts.filter(c => {
    const matchesSearch = searchQuery === '' ||
      c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.company?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || c.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const statCards = [
    { label: 'Total Leads', value: stats.total, icon: Users, color: 'from-blue-500 to-cyan-500' },
    { label: 'New', value: stats.new, icon: Inbox, color: 'from-violet-500 to-purple-500' },
    { label: 'Contacted', value: stats.contacted, icon: UserCheck, color: 'from-amber-500 to-orange-500' },
    { label: 'Converted', value: stats.converted, icon: TrendingUp, color: 'from-emerald-500 to-teal-500' },
  ];

  return (
    <div className="min-h-screen bg-vertex-950">
      {/* Header */}
      <header className="bg-vertex-900/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_SMALL} alt="Vertex" className="h-9 w-9 rounded-lg" />
            <div>
              <span className="text-white font-display font-bold text-base">VERTEX</span>
              <span className="text-vertex-400 font-display text-[10px] block -mt-0.5 tracking-widest">ADMIN</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchData} className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors" title="Refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white text-sm rounded-lg hover:bg-white/5 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map(s => (
            <div key={s.label} className="bg-vertex-900/50 border border-white/5 rounded-2xl p-5">
              <div className={`inline-flex p-2.5 rounded-xl bg-gradient-to-br ${s.color} mb-3`}>
                <s.icon className="w-4 h-4 text-white" />
              </div>
              <div className="text-2xl font-display font-bold text-white">{s.value}</div>
              <div className="text-gray-500 text-xs font-medium">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search leads by name, email, or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="admin-search"
              className="w-full pl-10 pr-4 py-2.5 bg-vertex-900/50 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-vertex-500/50 transition-all"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                filterStatus === 'all' ? 'bg-vertex-500 text-white' : 'bg-vertex-900/50 text-gray-400 border border-white/10 hover:text-white'
              }`}
            >
              All
            </button>
            {STATUS_OPTIONS.map(s => (
              <button
                key={s.value}
                onClick={() => setFilterStatus(s.value)}
                className={`px-4 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                  filterStatus === s.value ? `${s.bgLight} ${s.textColor}` : 'bg-vertex-900/50 text-gray-400 border border-white/10 hover:text-white'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Leads List */}
          <div className="lg:col-span-2 space-y-3">
            {loading && contacts.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin" />
                Loading leads...
              </div>
            ) : filteredContacts.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                <Inbox className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No leads found</p>
                <p className="text-xs mt-1">Leads will appear here when someone submits the contact form</p>
              </div>
            ) : (
              filteredContacts.map(contact => {
                const statusStyle = getStatusStyle(contact.status);
                const isSelected = selectedContact?.id === contact.id;
                return (
                  <div
                    key={contact.id}
                    onClick={() => { setSelectedContact(contact); setNotes(contact.notes || ''); }}
                    className={`bg-vertex-900/50 border rounded-xl p-5 cursor-pointer transition-all hover:border-vertex-500/30 ${
                      isSelected ? 'border-vertex-500/50 ring-1 ring-vertex-500/20' : 'border-white/5'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-white font-semibold text-sm">{contact.name}</h3>
                        <p className="text-gray-500 text-xs flex items-center gap-1 mt-0.5">
                          <Mail className="w-3 h-3" /> {contact.email}
                        </p>
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusStyle.bgLight} ${statusStyle.textColor}`}>
                        {statusStyle.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                      {contact.company && (
                        <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {contact.company}</span>
                      )}
                      {contact.phone && (
                        <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {contact.phone}</span>
                      )}
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(contact.timestamp)}</span>
                    </div>
                    {contact.message && (
                      <p className="text-gray-400 text-xs mt-3 line-clamp-2 leading-relaxed">
                        <MessageSquare className="w-3 h-3 inline mr-1 opacity-50" />
                        {contact.message}
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-1">
            {selectedContact ? (
              <div className="bg-vertex-900/50 border border-white/5 rounded-2xl p-6 sticky top-20">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-white font-display font-bold text-lg">{selectedContact.name}</h3>
                    <p className="text-gray-500 text-xs mt-0.5">{formatDate(selectedContact.timestamp)}</p>
                  </div>
                  <button
                    onClick={() => deleteContact(selectedContact.id)}
                    className="p-2 text-gray-500 hover:text-red-400 rounded-lg hover:bg-red-400/10 transition-colors"
                    title="Delete lead"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Contact Info */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-vertex-400" />
                    <a href={`mailto:${selectedContact.email}`} className="text-vertex-300 hover:underline">{selectedContact.email}</a>
                  </div>
                  {selectedContact.phone && (
                    <div className="flex items-center gap-3 text-sm">
                      <Phone className="w-4 h-4 text-vertex-400" />
                      <a href={`tel:${selectedContact.phone}`} className="text-gray-300">{selectedContact.phone}</a>
                    </div>
                  )}
                  {selectedContact.company && (
                    <div className="flex items-center gap-3 text-sm">
                      <Building2 className="w-4 h-4 text-vertex-400" />
                      <span className="text-gray-300">{selectedContact.company}</span>
                    </div>
                  )}
                </div>

                {/* Message */}
                {selectedContact.message && (
                  <div className="mb-6">
                    <label className="text-gray-500 text-xs font-medium mb-2 block">Message</label>
                    <div className="bg-white/5 rounded-xl p-4 text-gray-300 text-sm leading-relaxed">
                      {selectedContact.message}
                    </div>
                  </div>
                )}

                {/* Status */}
                <div className="mb-6">
                  <label className="text-gray-500 text-xs font-medium mb-2 block">Status</label>
                  <div className="grid grid-cols-2 gap-2">
                    {STATUS_OPTIONS.map(s => (
                      <button
                        key={s.value}
                        onClick={() => updateContact(selectedContact.id, { status: s.value })}
                        className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                          selectedContact.status === s.value
                            ? `${s.bgLight} ${s.textColor} ring-1 ring-current`
                            : 'bg-white/5 text-gray-400 hover:text-white'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-gray-500 text-xs font-medium mb-2 block">Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-vertex-500/50 transition-all resize-none"
                    placeholder="Add private notes about this lead..."
                  />
                  <button
                    onClick={() => updateContact(selectedContact.id, { notes })}
                    className="mt-2 w-full px-4 py-2 bg-vertex-500 hover:bg-vertex-600 text-white text-xs font-semibold rounded-lg transition-all"
                  >
                    Save Notes
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-vertex-900/50 border border-white/5 rounded-2xl p-10 text-center">
                <Users className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 text-sm font-medium">Select a lead</p>
                <p className="text-gray-600 text-xs mt-1">Click on a lead to view details and manage it</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
